import styled from 'styled-components';
import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RankItem } from './components/AuctionRank';
import type { WeeklyTrendItem } from './components/PeriodAuctionTrends';
import type { RegionMetricsItem } from './components/RegionAuctionOverview';
import type { TrendRow } from './components/graph/AuctionTrendAnalysisGraph';
import type { MonthlyTrend } from './components/NationalAuctionDelta';
import { format, endOfISOWeek, startOfISOWeek, addWeeks } from 'date-fns';
import SummaryCards from './components/SummaryCards';
import AuctionRank from './components/AuctionRank';
import PeriodAuctionTrend from './components/PeriodAuctionTrends';
import UsageAuctionOverview from './components/UsageAuctionOverview';
import RegionAuctionOverview from './components/RegionAuctionOverview';
import AuctionTrendsAnalysis from './components/AuctionTrendsAnalysis';
import AnnualNationalAuctionTrend from './components/AnnualNationalAuctionTrend';
import NationalAuctionDelta from './components/NationalAuctionDelta';
import CommonFilter from './components/filter/CommonFilter';
import PageTop from '../components/pageTop/PageTop';
import { PageContainer } from '../components/common/PageComponent.style';
import MembershipDescription from './components/MembershipDescription';
import { useUser } from '../../context/UserContext';

const PeriodStats = () => {
  const navigate = useNavigate();

  const { user } = useUser();

  const url = new URL(window.location.href);
  const token = url.searchParams.get('token');

  const plan = token ? 'P' : user?.plan_grade;
  // const plan = user?.plan_grade; // 'P' | 'S' | 기타(undefined 등)
  const disableWeekly = plan !== 'P'; // P만 주간 허용, S/기타는 주간 금지
  const disableAllFilters = plan !== 'P' && plan !== 'S'; // S도 필터는 일부 허용
  const disablePeriodOnly = plan === 'S'; // S는 기간만 금지, 나머진 허용
  const [showMembershipDesc, setShowMembershipDesc] = useState(false);

  const [weeklyAlertOpen, setWeeklyAlertOpen] = useState(false);
  const getPlanAlertContent = (plan?: any) => {
    if (plan === 'S') {
      return {
        title: '월간 통계만 확인할 수 있습니다.',
        desc: '더 많은 통계를 원하신다면\n요금제를 업그레이드 해주세요.',
        gray: '요금제 둘러보기',
        blue: '월간 통계 확인',
        onGray: () => navigate('/payment/plan'),
      };
    }
    if (plan === 'P') return null;
    return {
      title: '샘플 통계만 확인할 수 있습니다.',
      desc: '더 많은 통계를 원하신다면\n요금제를 업그레이드 해주세요.',
      gray: '요금제 둘러보기',
      blue: '샘플 통계 확인',
      onGray: () => navigate('/payment/plan'),
    };
  };

  const [analysisPeriod, setAnalysisPeriod] = useState<
    'week' | 'month' | 'year'
  >('month');

  const toYMD = (d: Date) => format(d, 'yyyy-MM-dd');
  const today = new Date();
  const prevMonthFirst = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const [date, setDate] = useState<{ from: Date; to: Date }>({
    from: prevMonthFirst,
    to: prevMonthEnd,
  });
  const [filterType, setFilterType] = useState('court');
  const [court, setCourt] = useState('all');
  const [regionCode, setRegionCode] = useState<string>(''); // API용
  const [regionName, setRegionName] = useState<string>('전체'); // 표시/하위 컴포넌트용
  const [usage, setUsage] = useState('전체');
  const [usageDetail, setUsageDetail] = useState('전체');

  const [isDetail, setIsDetail] = useState<boolean>(false); // 상세 용도 전환 여부

  const normalizeAll = (v?: string) =>
    v === 'all' || v === '전체' ? '' : (v ?? '');

  const regionParam = normalizeAll(regionCode);
  const usageParam = normalizeAll(usage);
  const usageDetailParam = normalizeAll(usageDetail);
  const effectiveUsageParam = usageDetailParam || usageParam;
  const courtParam = normalizeAll(court);
  const [rankType, setRankType] = useState<'A' | 'B' | 'C' | 'D'>('A');

  const [rankData, setRankData] = useState<RankItem[]>([]);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrendItem[]>([]);
  const [weeklyLoading, setWeeklyLoading] = useState<boolean>(false);
  const [weeklyError, setWeeklyError] = useState<string | null>(null);

  const [regionalStats, setRegionalStats] = useState<RegionMetricsItem[]>([]);
  const [regionalLoading, setRegionalLoading] = useState(false);
  const [regionalError, setRegionalError] = useState<string | null>(null);

  const [trendRows, setTrendRows] = useState<TrendRow[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [monthlyTrendData, setMonthlyTrendData] = useState<MonthlyTrend[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState<string | null>(null);

  const [usageRows, setUsageRows] = useState<any[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  const [rankingRaw, setRankingRaw] = useState<any[]>([]);
  const [insightText, setInsightText] = useState<string>('');
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState<string | null>(null);

  const [monthlyInsight, setMonthlyInsight] = useState<any>(null);
  const [monthlyInsightLoading, setMonthlyInsightLoading] = useState(false);
  const [monthlyInsightError, setMonthlyInsightError] = useState<string | null>(
    null,
  );

  function getMonthWeekNumber(date: Date): number {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfWeek = firstDayOfMonth.getDay(); // 0:일 ~ 6:토
    const offsetDate = date.getDate() + dayOfWeek - 1;
    return Math.ceil(offsetDate / 7);
  }

  const periodMonthLabel = useMemo(() => {
    const d = date.from;
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
  }, [date.from]);

  const periodWeekLabel = useMemo(() => {
    const d = date.from;
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${getMonthWeekNumber(d)}주차`;
  }, [date.from]);

  const periodSummary =
    analysisPeriod === 'week' ? periodWeekLabel : periodMonthLabel;

  const mmdd = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

  const toSummaryData = (obj?: {
    progressed_items?: any;
    successful_items?: any;
    success_rate?: any;
    price_success_rate?: any;
    avg_bidders?: any;
  }) => {
    if (!obj) return null;
    return {
      progressed_items: obj.progressed_items ?? 0,
      successful_items: obj.successful_items ?? 0,
      success_rate: obj.success_rate ?? 0,
      price_success_rate: obj.price_success_rate ?? 0,
      avg_bidders: obj.avg_bidders ?? 0,
    };
  };

  const summaryWeekData = useMemo(() => {
    if (!weeklyTrend || weeklyTrend.length === 0) return null;
    const first = weeklyTrend[0]; // { progress, wonCount, winRate, winPriceRate, avgBidders }
    return toSummaryData({
      progressed_items: first.progress,
      successful_items: first.wonCount,
      success_rate: first.winRate,
      price_success_rate: first.winPriceRate,
      avg_bidders: first.avgBidders,
    });
  }, [weeklyTrend]);

  const summaryMonthData = useMemo(() => {
    if (!monthlyTrendData || monthlyTrendData.length === 0) return null;
    const last = monthlyTrendData[monthlyTrendData.length - 1];
    return toSummaryData({
      progressed_items: last.progressed_items,
      successful_items: last.successful_items,
      success_rate: last.success_rate,
      price_success_rate: last.price_success_rate,
      avg_bidders: last.avg_bidders,
    });
  }, [monthlyTrendData]);

  // 주/월, 날짜, 지역, 용도, 탭 변경 시 작동
  useEffect(() => {
    setRankingLoading(true);
    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}zipgo/ranking`, {
        params: {
          start: toYMD(date.from),
          end: toYMD(date.to),
          ...(regionParam ? { region: regionParam } : {}),
          ...(effectiveUsageParam ? { usage: effectiveUsageParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
          limit: 30,
          type: rankType,
        },
      })
      .then(({ data }) => {
        const rawRows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
        setRankingRaw(rawRows);

        const mapped: RankItem[] = (Array.isArray(data) ? data : []).map(
          (row: any, idx: number) => ({
            id: `${row.case_number ?? idx}`,
            caseNo: String(row.case_number ?? ''),
            address: String(row.location ?? ''),
            usage: String(row.use ?? '').trim(),
            appraisal: Number(row.appraised_value ?? 0),
            winning: Number(row.winning_bid ?? 0),
            winRate: (() => {
              const v = row.bid_rate;
              const n = typeof v === 'string' ? parseFloat(v) : Number(v);
              return isNaN(n) ? 0 : n / 100;
            })(),
            bidders:
              row.bidders === null || row.bidders === undefined
                ? null
                : Number(row.bidders),
          }),
        );
        setRankData(mapped);
      })
      .finally(() => setRankingLoading(false));
  }, [date, regionCode, usage, usageDetail, court, rankType]);

  useEffect(() => {
    if (!rankingRaw || rankingRaw.length === 0) {
      setInsightText('');
      return;
    }

    setInsightLoading(true);
    setInsightError(null);

    axios
      .post(`${process.env.REACT_APP_HOST_API_STATS}zipgo/ranking/insight`, {
        data: rankingRaw, // 가공 전 원본 그대로
        type: rankType, // AuctionRank에서 쓰는 type
        limit: 30,
      })
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : (data?.data ?? []);
        const paragraphs = (arr ?? [])
          .map((it: any) => {
            const emoji = String(it?.emoji ?? '').trim();
            const text = String(it?.text ?? '')
              .replace(/\s*\n+\s*/g, ' ')
              .trim();
            // 이모지 + 공백 + 텍스트 (같은 줄)
            return [emoji, text].filter(Boolean).join(' ');
          })
          .filter(Boolean);

        setInsightText(paragraphs.join('\n\n'));
      })
      .catch((e) => {
        console.error(e);
        setInsightText('');
        setInsightError('랭킹 인사이트를 불러오지 못했어요.');
      })
      .finally(() => setInsightLoading(false));
  }, [date, regionCode, usage, usageDetail, court, rankingRaw, rankType]);

  useEffect(() => {
    setUsageLoading(true);
    setUsageError(null);

    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}api/category-summary`, {
        params: {
          startDate: toYMD(date.from), // = start
          endDate: toYMD(date.to), // = end
          ...(regionParam ? { region: regionParam } : {}),
          ...(effectiveUsageParam ? { usage: effectiveUsageParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
          isDetail: isDetail,
        },
      })
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.data ?? []);
        setUsageRows(rows);
      })
      .catch((e) => {
        console.error(e);
        setUsageRows([]);
        setUsageError('용도별 지표를 불러오지 못했어요.');
      })
      .finally(() => setUsageLoading(false));
  }, [date, regionCode, usage, usageDetail, court, isDetail]);

  // 주/월, 날짜, 지역, 용도 변경 시 작동
  useEffect(() => {
    if (analysisPeriod !== 'week') return;

    setWeeklyLoading(true);
    setWeeklyError(null);

    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}zipgo/weekly-trend`, {
        params: {
          end: toYMD(date.to),
          ...(regionParam ? { region: regionParam } : {}),
          ...(effectiveUsageParam ? { usage: effectiveUsageParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
        },
      })
      .then(({ data }) => {
        // data 형태에 방어적으로 매핑 (키 이름이 조금 달라도 수용)
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);

        const mapped: WeeklyTrendItem[] = rows.map((r: any, idx: number) => {
          // 주차 레이블 만들기: API가 주차 시작/끝일을 주면 그걸 쓰고,
          // 없으면 period/label 필드를, 그것도 없으면 end 기준으로 ISO 주차 계산
          const start =
            (r.week_start ?? r.start ?? r.start_date)
              ? new Date(r.week_start ?? r.start ?? r.start_date)
              : startOfISOWeek(new Date(r.date ?? r.end ?? date.to));
          const end =
            (r.week_end ?? r.end ?? r.end_date)
              ? new Date(r.week_end ?? r.end ?? r.end_date)
              : endOfISOWeek(new Date(r.date ?? date.to));

          const periodLabel =
            r.period ?? r.label ?? `${mmdd(start)}~${mmdd(end)}`;

          return {
            id: String(r.id ?? idx),
            week: String(r.week ?? r.week_no ?? ''), // 있으면 표기
            period: periodLabel,
            progress: Number(r.progressed_items ?? r.progress ?? 0),
            wonCount: Number(r.successful_items ?? r.wonCount ?? 0),
            winRate: Number(
              // 퍼센트(%) 값으로 표준화
              r.success_rate ??
                r.winRate ??
                (typeof r.bid_success_rate === 'number'
                  ? r.bid_success_rate * 100
                  : (r.bid_success_rate ?? 0)),
            ),
            winPriceRate: Number(r.price_success_rate ?? r.winPriceRate ?? 0),
            avgBidders: Number(r.avg_bidders ?? r.avgBidders ?? 0),
          };
        });

        setWeeklyTrend(mapped);
      })
      .catch((err) => {
        console.error(err);
        setWeeklyError('주간 동향 데이터를 불러오지 못했어요.');
        setWeeklyTrend([]);
      })
      .finally(() => setWeeklyLoading(false));
  }, [analysisPeriod, date, regionCode, usage, usageDetail, court]);

  // 주/월, 날짜, 지역, 용도 변경 시 작동
  useEffect(() => {
    setTrendLoading(true);
    setTrendError(null);

    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}zipgo/trend`, {
        params: {
          type: analysisPeriod, // 'week' | 'month' | 'year'
          start: toYMD(date.from),
          end: toYMD(date.to),
          ...(regionParam ? { region: regionParam } : {}),
          ...(usageDetailParam ? { usageDetail: usageDetailParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
        },
      })
      .then(({ data }) => {
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);

        // 포맷 유틸
        const pad2 = (n: number) => String(n).padStart(2, '0');
        const mmddDot = (mmddSlash: string) => {
          // "MM/DD" -> "MM.DD"
          const [m, d] = mmddSlash.split('/');
          return `${pad2(Number(m))}.${pad2(Number(d))}`;
        };

        const normalizeNumber = (v: unknown) => {
          // "1721", 1721, "62.7" 모두 숫자로 변환
          if (v === null || v === undefined) return 0;
          if (typeof v === 'number') return v;
          const n = Number(String(v).replace(/,/g, '')); // 혹시 콤마 들어오면 제거
          return isNaN(n) ? 0 : n;
        };

        const mapped: TrendRow[] = rows.map((r: any, idx: number) => {
          let label = String(idx);

          if (analysisPeriod === 'week') {
            // week 응답 예시:
            // { period: "07/21~07/27", week: "2025년 30주차", ... }
            // 차트 라벨은 "~MM.DD" 형태로 통일
            const periodStr: string = r.period ?? '';
            // "07/21~07/27" -> "~07.27"
            if (periodStr.includes('~')) {
              const endPart = periodStr.split('~')[1]?.trim() ?? '';
              label = `~${mmddDot(endPart)}`;
            } else {
              // period가 없을 경우 week나 기타를 최소 표기
              label = r.week ? String(r.week) : `~${idx + 1}`;
            }
          } else if (analysisPeriod === 'month') {
            // month 응답 예시:
            // { month: "2025-09", period: "2025-09", ... }
            const ym = String(r.period ?? r.month ?? '');
            // "YYYY-MM" -> "YYYY.MM"
            label = ym.replace('-', '.');
          } else {
            // year 등 확장 대비 (현재 스키마가 없으므로 period/label 우선)
            label = String(r.label ?? r.period ?? r.year ?? idx);
          }

          return {
            label,
            progress: normalizeNumber(r.progressed_items),
            wonCount: normalizeNumber(r.successful_items),
            // success_rate, price_success_rate는 이미 "퍼센트 값" (예: "62.7")
            winRate: normalizeNumber(r.success_rate),
            winPriceRate: normalizeNumber(r.price_success_rate),
            avgBidders: normalizeNumber(r.avg_bidders),
          } as TrendRow;
        });

        setTrendRows(mapped);
      })
      .catch((err) => {
        console.error(err);
        setTrendError('경매 동향 데이터를 불러오지 못했어요.');
        setTrendRows([]);
      })
      .finally(() => setTrendLoading(false));
  }, [analysisPeriod, date, regionCode, usage, usageDetail, court]);

  // 주/월, 날짜 변경 시 작동
  useEffect(() => {
    setRegionalLoading(true);
    setRegionalError(null);

    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}api/regional-stats`, {
        params: {
          startDate: toYMD(date.from),
          endDate: toYMD(date.to),
          ...(regionParam ? { region: regionParam } : {}),
          ...(effectiveUsageParam ? { usage: effectiveUsageParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
        },
      })
      .then(({ data }) => {
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);

        const mapped: RegionMetricsItem[] = rows.map((r: any, idx: number) => ({
          id: String(r.id ?? r.code ?? idx),
          usage: String(r.region_name ?? r.region ?? r.name ?? r.usage ?? ''),
          progress: Number(r.progressed_items ?? r.progress ?? 0),
          wonCount: Number(r.successful_items ?? r.wonCount ?? 0),
          winRate: Number(
            r.success_rate ??
              r.winRate ??
              (typeof r.bid_success_rate === 'number'
                ? r.bid_success_rate * 100
                : (r.bid_success_rate ?? 0)),
          ),
          winPriceRate: Number(r.price_success_rate ?? r.winPriceRate ?? 0),
          avgBidders: Number(r.avg_bidders ?? r.avgBidders ?? 0),
        }));

        setRegionalStats(mapped);
      })
      .catch((err) => {
        console.error(err);
        setRegionalError('지역별 지표를 불러오지 못했어요.');
        setRegionalStats([]);
      })
      .finally(() => setRegionalLoading(false));
  }, [analysisPeriod, date, regionCode, usage, usageDetail, court]);

  useEffect(() => {
    if (analysisPeriod !== 'month') return;

    setMonthlyLoading(true);
    setMonthlyError(null);

    axios
      .get(`${process.env.REACT_APP_HOST_API_STATS}zipgo/monthly-trend`, {
        params: {
          start: toYMD(date.from),
          end: toYMD(date.to),
          ...(regionParam ? { region: regionParam } : {}),
          ...(effectiveUsageParam ? { usage: effectiveUsageParam } : {}),
          ...(courtParam ? { court: courtParam } : {}),
        },
      })
      .then(({ data }) => {
        const rows: any[] = Array.isArray(data) ? data : (data?.data ?? []);
        setMonthlyTrendData(rows as MonthlyTrend[]); // ← 그대로 저장
      })
      .catch((e) => {
        console.error(e);
        setMonthlyTrendData([]);
        setMonthlyError('월간 동향 데이터를 불러오지 못했어요.');
      })
      .finally(() => setMonthlyLoading(false));
  }, [analysisPeriod, date, regionCode, usage, usageDetail, court]);

  useEffect(() => {
    // 월간 모드가 아니거나, 원본이 비어있으면 스킵
    if (analysisPeriod !== 'month') return;
    if (!Array.isArray(monthlyTrendData) || monthlyTrendData.length === 0)
      return;

    let cancelled = false;
    setMonthlyInsightLoading(true);
    setMonthlyInsightError(null);

    axios
      .post(
        `${process.env.REACT_APP_HOST_API_STATS}zipgo/monthly-trend/insight`,
        {
          data: monthlyTrendData,
        },
      )
      .then(({ data }) => {
        if (cancelled) return;
        // 응답 형태 콘솔 확인
        const rows = Array.isArray(data) ? data : (data?.data ?? data ?? []);
        setMonthlyInsight(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[monthly-trend/insight] fetch error:', err);
      })
      .finally(() => {
        if (cancelled) return;
        setMonthlyInsightLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [analysisPeriod, monthlyTrendData]);

  return (
    <PageContainer $bg="#F4F5F7">
      <PageTop text="월간/주간 통계" token={token} hasSearch={false} />
      <TabBox>
        <Tab
          $active={analysisPeriod === 'month'}
          onClick={() => {
            setAnalysisPeriod('month');
            const now = new Date();
            const prevFirst = new Date(
              now.getFullYear(),
              now.getMonth() - 1,
              1,
            );
            const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0);
            setDate({ from: prevFirst, to: prevEnd }); // 전월로 즉시 갱신
          }}
          aria-selected={analysisPeriod === 'month'}
          role="tab"
        >
          월간 통계
        </Tab>
        <Tab
          $active={analysisPeriod === 'week'}
          $disabled={disableWeekly}
          onClick={() => {
            if (disableWeekly) {
              setWeeklyAlertOpen(true);
              return;
            }
            setAnalysisPeriod('week');
            const now = new Date();
            const lastWeekStart = startOfISOWeek(addWeeks(now, -1));
            const lastWeekEnd = endOfISOWeek(addWeeks(now, -1));
            setDate({ from: lastWeekStart, to: lastWeekEnd });
          }}
          aria-disabled={disableWeekly}
          role="tab"
        >
          주간 통계
        </Tab>
      </TabBox>
      <MembershipDescription
        open={(!user || !plan || plan === 'S') && !token}
        onClose={() => setShowMembershipDesc(false)}
        user={user}
        planGrade={plan}
      />
      <Section>
        <CommonFilter
          analysisPeriod={analysisPeriod}
          setAnalysisPeriod={setAnalysisPeriod}
          date={date}
          setDate={setDate}
          filterType={filterType}
          setFilterType={setFilterType}
          court={court}
          setCourt={setCourt}
          regionCode={regionCode}
          setRegionCode={setRegionCode}
          regionName={regionName}
          setRegionName={setRegionName}
          usage={usage}
          setUsage={setUsage}
          usageDetail={usageDetail}
          setUsageDetail={setUsageDetail}
          disableAll={disableAllFilters}
        />

        <SummaryCards
          court={court}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          date={date}
          type={analysisPeriod}
          summaryData={
            analysisPeriod === 'week' ? summaryWeekData : summaryMonthData
          }
          isLoading={analysisPeriod === 'week' ? weeklyLoading : monthlyLoading}
        />
      </Section>

      <Content>
        {analysisPeriod === 'month' && (
          <NationalAuctionDelta
            data={monthlyTrendData}
            loading={monthlyLoading}
            error={monthlyError}
            periodType={'month'}
            insights={monthlyInsight}
            insightsLoading={monthlyInsightLoading}
            insightsError={monthlyInsightError}
          />
        )}
        {analysisPeriod === 'week' && (
          // 주간 경매 동향
          <PeriodAuctionTrend
            periodSummary={periodSummary}
            region={regionName}
            usage={usage}
            data={weeklyTrend}
            loading={weeklyLoading}
            error={weeklyError}
          />
        )}
        {/* 주요 경매 물건 순위 */}
        <AuctionRank
          periodSummary={periodSummary}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          type={analysisPeriod}
          selectedType={rankType}
          onTypeChange={setRankType}
          data={rankData}
          loading={rankingLoading}
          insights={insightText}
          insightsLoading={insightLoading}
          insightsError={insightError}
        />

        {/* 경매 동향 분석 */}
        <AuctionTrendsAnalysis
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          data={trendRows}
          loading={trendLoading}
          error={trendError}
        />
        {/* 연간 전국 경매 지표 동향 */}
        <AnnualNationalAuctionTrend
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          type={analysisPeriod}
          data={trendRows}
          loading={trendLoading}
          error={trendError}
        />

        {/* 용도별 경매 지표 현황 */}
        <UsageAuctionOverview
          periodSummary={periodSummary}
          region={regionName}
          isDetail={isDetail}
          onToggleDetail={() => setIsDetail((v) => !v)}
          usage={usage}
          usageDetail={usageDetail}
          court={court}
          usageData={usageRows}
          loading={usageLoading}
          error={usageError}
        />
        {/* 지역별 경매 지표 현황 */}
        <RegionAuctionOverview
          periodSummary={periodSummary}
          region={regionName}
          usage={usage}
          usageDetail={usageDetail}
          data={regionalStats}
          loading={regionalLoading}
          error={regionalError}
        />
      </Content>
    </PageContainer>
  );
};

export default PeriodStats;

const TabBox = styled.div`
  display: flex;
  align-items: flex-start;
  flex: 1 0 0;
  border-bottom: 1px solid #e5e8ea;
  background: #fff;

  position: sticky;
  top: 56px;
  z-index: 10;
`;

const Tab = styled.button<{ $active: boolean; $disabled?: boolean }>`
  appearance: none;
  border: none;
  outline: none;

  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex: 1 0 0;
  align-self: stretch;
  background: #fff;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? '700' : '500')};
  line-height: 145%;
  letter-spacing: -0.28px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  color: ${({ $active }) => ($active ? '#0C0E12' : '#A7ADB8')};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? '#0C0E12' : 'transparent')};
  display: inline-block;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
`;

const Section = styled.div`
  display: flex;
  padding: 16px 16px 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;
